import { Button, Text } from '@southfields-digital/mpxlive-components';

import { useDataConnector } from '../../hooks/useDataConnector';
import { usePreviewData } from '../../hooks/usePreviewData';

import styles from './DataConnectorPanel.module.scss';
import { ErrorDisplay } from './ErrorDisplay';
import { LoadingSection } from './LoadingSection';
import { PreviewSection } from './PreviewSection';
import { RuntimeFieldsSection } from './RuntimeFieldsSection';

type DataConnectorPanelProps = {
  selectedGraphic: PlaylistGraphic;
  onImport: (propertyValues: PropertyValue) => void;
};

export default function DataConnectorPanel({ selectedGraphic, onImport }: DataConnectorPanelProps) {
  const {
    state,
    designTimeContext,
    schemaFormData,
    runtimeContext,
    updateRuntimeContext,
    fetchData,
    canFetch,
    schema,
  } = useDataConnector({
    graphic: selectedGraphic.graphic,
  });

  const preview = usePreviewData({
    graphic: selectedGraphic.graphic,
    rawData: state.status === 'success' ? state.data : undefined,
    currentPropertyValues: selectedGraphic.propertiesValues,
  });

  const isLoading = state.status === 'fetching' || state.status === 'loading-schema';
  const error = state.status === 'error' ? state.error : undefined;

  const dataConnector = selectedGraphic.graphic.config.dataConnector;

  const handleImport = () => {
    const injectedValues = preview.getInjectedValues();
    onImport(injectedValues);
    preview.clearPreview();
  };

  const handleUpdateProperty = (propertyId: string, value: string | PropertyValue[]) => {
    preview.updatePreview({ propertyId, value });
  };

  if (dataConnector === undefined) {
    return (
      <div className={styles.Container}>
        <Text as="p">This graphic does not have a data connector configured.</Text>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <Text as="h2" className={styles.Title}>
        Data Connector
      </Text>

      {error && <ErrorDisplay error={error} />}

      <div className={styles.Content}>
        {schema && (
          <RuntimeFieldsSection
            schema={schema}
            formData={schemaFormData}
            designTimeContext={designTimeContext}
            runtimeContext={runtimeContext}
            onContextChange={updateRuntimeContext}
            isDisabled={isLoading}
          />
        )}

        <Button variant="primary" onClick={fetchData} disabled={!canFetch || isLoading}>
          Fetch Data
        </Button>

        {isLoading && <LoadingSection />}

        {preview.previewData && state.status === 'success' && (
          <PreviewSection
            graphic={selectedGraphic.graphic}
            previewData={preview.previewData}
            onImport={handleImport}
            onUpdateProperty={handleUpdateProperty}
          />
        )}
      </div>
    </div>
  );
}
